import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';

import {DataCaptureDraftDocument} from 'llm/data-capture/DataCaptureDraftDocument';
import {GRAY, GRAY_LIGHT, GRAY_LIGHTER, hexToRgba} from 'ui-library/Colors';

import {DraftDocument} from './DataCaptureTypes';

export interface DataCaptureSideBySideLayoutProps {
  chatContent: JSX.Element;
  draftDocument: DraftDocument;
}

export function DataCaptureSideBySideLayout({
  chatContent,
  draftDocument,
}: DataCaptureSideBySideLayoutProps): JSX.Element {
  const [isDragging, setIsDragging] = useState(false);

  return (
    <div className={css(styles.sideBySideContainer)} data-testid="side-by-side-container">
      <PanelGroup direction="horizontal" style={{width: '100%', height: '100%'}}>
        <Panel defaultSize={50} minSize={20} maxSize={80} className={css(styles.chatPanel)}>
          <div data-testid="chat-panel" className={css(styles.panelWrapper)}>
            <div className={css(styles.scrollableContent)} data-testid="scrollable-content">
              {chatContent}
            </div>
          </div>
        </Panel>

        <PanelResizeHandle
          className={css(styles.resizer, isDragging && styles.resizerActive)}
          onDragging={setIsDragging}
        >
          <div data-testid="resizer" className={css(styles.resizerWrapper)}>
            <div className={css(styles.resizerHandle)} data-testid="resizer-handle" />
          </div>
        </PanelResizeHandle>

        <Panel defaultSize={50} minSize={20} maxSize={80} className={css(styles.documentPanel)}>
          <div data-testid="document-panel" className={css(styles.panelWrapper)}>
            <div className={css(styles.scrollableContent)} data-testid="scrollable-content">
              <DataCaptureDraftDocument draftDocument={draftDocument} />
            </div>
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
}

const styles = StyleSheet.create({
  sideBySideContainer: {
    display: 'flex',
    height: '100%',
    position: 'relative',
    backgroundColor: GRAY_LIGHTER,
    width: '100%',
  },
  chatPanel: {
    height: '100%',
  },
  documentPanel: {
    height: '100%',
  },
  panelWrapper: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'white',
    boxShadow: `0px 1px 3px 0px rgba(${hexToRgba(GRAY, 0.2)})`,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%',
    padding: '8px',
  },
  resizer: {
    width: '12px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s ease',
    ':hover': {
      backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.3)})`,
    },
  },
  resizerWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resizerActive: {
    backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.5)})`,
  },
  resizerHandle: {
    width: '2px',
    height: '40px',
    backgroundColor: GRAY_LIGHT,
    borderRadius: '2px',
    transition: 'all 0.15s ease',
  },
});
